import type { ScheduleComponent } from '@syncfusion/ej2-react-schedule'
import { toSiteWallClockDate } from 'common/services/dateService'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { type RefObject, useEffect, useRef, useState } from 'react'
import { type AppointmentColors, applyAppointmentColors } from './applyAppointmentColors'
import {
  countScheduleEventChanges,
  diffScheduleEvents,
  fingerprintSchedulePositions,
  getScheduleEngineEvents,
  needsScheduleCollectionRefresh,
  patchLiveScheduleEvents,
  patchScheduleEngineEvents,
  replaceScheduleEngineEvents,
} from './diffScheduleEvents'
import { canRefreshScheduleEvents, safeRefreshScheduleEvents } from './safeRefreshScheduleEvents'
import { scheduleEventStore } from './scheduleEventStore'

const WHOLESALE_CHANGE_RATIO = 0.5

const applyColors = (
  scheduleElement: HTMLElement,
  event: IPatientSchedule,
  getColors: (event: IPatientSchedule) => AppointmentColors,
) => {
  const colors = getColors(event)
  scheduleElement
    .querySelectorAll<HTMLElement>(
      `[data-id="Appointment_${event.id}"]:not(.e-schedule-event-clone)`,
    )
    .forEach((element) => applyAppointmentColors(element, colors))
}

const isTabVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible'

type UseSilentEventSyncParams = {
  scheduleObj: RefObject<ScheduleComponent | null>
  data: IPatientSchedule[]
  getEventColors: (event: IPatientSchedule) => AppointmentColors
  timeZone?: string
  instanceKey?: string
  onPatched?: () => void
}

const toEngineEvents = (events: IPatientSchedule[], timeZone?: string): IPatientSchedule[] => {
  if (!timeZone) return events
  return events.map((event) => ({
    ...event,
    start_time: toSiteWallClockDate(event.start_time, timeZone) ?? event.start_time,
    end_time: toSiteWallClockDate(event.end_time, timeZone) ?? event.end_time,
  }))
}

export const useSilentEventSync = ({
  scheduleObj,
  data,
  getEventColors,
  timeZone,
  instanceKey,
  onPatched,
}: UseSilentEventSyncParams): IPatientSchedule[] => {
  const [dataSource, setDataSource] = useState<IPatientSchedule[]>(data)
  const appliedRef = useRef<IPatientSchedule[] | null>(null)
  const dataRef = useRef(data)
  dataRef.current = data
  const dataSourceRef = useRef<IPatientSchedule[]>(dataSource)
  dataSourceRef.current = dataSource
  const paintedPositionsRef = useRef('')
  const getColorsRef = useRef(getEventColors)
  getColorsRef.current = getEventColors
  const onPatchedRef = useRef(onPatched)
  onPatchedRef.current = onPatched
  const instanceKeyRef = useRef(instanceKey)
  const paintRetryRafRef = useRef<number | undefined>(undefined)
  const pendingPositionPaintRef = useRef<IPatientSchedule[] | null>(null)
  const rebindEngineRef = useRef(false)
  const timeZoneRef = useRef(timeZone)
  timeZoneRef.current = timeZone

  if (instanceKeyRef.current !== instanceKey) {
    instanceKeyRef.current = instanceKey
    appliedRef.current = null
    paintedPositionsRef.current = ''
    pendingPositionPaintRef.current = null
    rebindEngineRef.current = false
    setDataSource(data)
  }

  const paintPositions = (events: IPatientSchedule[]) => {
    const schedule = scheduleObj.current
    if (!isTabVisible()) return false
    if (!canRefreshScheduleEvents(schedule)) return false

    if (rebindEngineRef.current) {
      replaceScheduleEngineEvents(schedule, events)
      if (!safeRefreshScheduleEvents(schedule, true)) return false
      rebindEngineRef.current = false
    } else {
      const engineUpdates = toEngineEvents(events, timeZoneRef.current)
      patchLiveScheduleEvents(dataSourceRef.current, events)

      const engineEvents = (schedule as unknown as { eventsData?: { id?: number | string }[] })
        ?.eventsData
      if (engineEvents) {
        const { missingIds } = patchLiveScheduleEvents(
          engineEvents as IPatientSchedule[],
          engineUpdates,
        )
        if (missingIds.length > 0) {
          const missingSet = new Set(missingIds)
          for (const event of engineUpdates) {
            if (missingSet.has(Number(event.id))) {
              engineEvents.push({ ...event } as unknown as (typeof engineEvents)[number])
            }
          }
        }
      }

      if (!safeRefreshScheduleEvents(schedule, true)) return false
    }

    paintedPositionsRef.current = fingerprintSchedulePositions(events)
    onPatchedRef.current?.()
    if (schedule?.element) {
      for (const event of events) {
        applyColors(schedule.element, event, getColorsRef.current)
      }
    }
    return true
  }

  const schedulePaintRetry = (events: IPatientSchedule[], attempts = 0) => {
    if (paintRetryRafRef.current) {
      cancelAnimationFrame(paintRetryRafRef.current)
      paintRetryRafRef.current = undefined
    }
    if (paintPositions(events)) return
    if (attempts >= 30) return
    paintRetryRafRef.current = requestAnimationFrame(() => {
      schedulePaintRetry(events, attempts + 1)
    })
  }

  const syncVisiblePositions = () => {
    if (!isTabVisible()) return
    const events = dataRef.current
    if (fingerprintSchedulePositions(events) === paintedPositionsRef.current) return
    patchLiveScheduleEvents(dataSourceRef.current, events)
    for (const event of events) scheduleEventStore.set(event.id, event, instanceKeyRef.current)
    paintPositions(events)
  }
  const syncVisiblePositionsRef = useRef(syncVisiblePositions)
  syncVisiblePositionsRef.current = syncVisiblePositions

  useEffect(() => {
    if (appliedRef.current === data) return

    const schedule = scheduleObj.current
    const prev = appliedRef.current
    appliedRef.current = data

    const fullRefresh = () => {
      rebindEngineRef.current = true
      scheduleEventStore.hydrate(data, instanceKeyRef.current)
      const next = data.map((event) => ({ ...event }))
      dataSourceRef.current = next
      // Syncfusion may refresh before React commits `eventSettings.dataSource`.
      // Queue the repaint and run it after `dataSource` commit.
      pendingPositionPaintRef.current = next
      setDataSource(next)
    }

    if (prev == null) {
      scheduleEventStore.hydrate(data, instanceKeyRef.current)
      setDataSource(data)
      schedulePaintRetry(data)
      return
    }

    if (!schedule || (schedule as { isDestroyed?: boolean }).isDestroyed) {
      fullRefresh()
      return
    }

    const diff = diffScheduleEvents(prev, data)
    const changeCount = countScheduleEventChanges(diff)
    if (changeCount === 0) {
      for (const event of data) scheduleEventStore.set(event.id, event, instanceKeyRef.current)
      return
    }

    const isWholesale =
      prev.length === 0 || changeCount > Math.max(prev.length, data.length) * WHOLESALE_CHANGE_RATIO
    if (isWholesale || needsScheduleCollectionRefresh(diff)) {
      for (const event of diff.removed) {
        scheduleEventStore.remove(event.id, instanceKeyRef.current)
      }
      fullRefresh()
      return
    }

    if (diff.positionChanged.length > 0) {
      const updates = [...diff.positionChanged, ...diff.contentChanged]
      patchLiveScheduleEvents(dataSourceRef.current, updates)

      const incomingPositions = fingerprintSchedulePositions(data)
      const engineUpdates = toEngineEvents(updates, timeZoneRef.current)
      const engineEvents = getScheduleEngineEvents(schedule) as IPatientSchedule[] | undefined
      const engineAlreadyCurrent =
        !!engineEvents?.length &&
        fingerprintSchedulePositions(engineEvents) ===
          fingerprintSchedulePositions(toEngineEvents(data, timeZoneRef.current))

      if (!engineAlreadyCurrent) {
        const { missingIds } = patchScheduleEngineEvents(schedule, engineUpdates)
        if (missingIds.length > 0) {
          fullRefresh()
          return
        }
        if (!safeRefreshScheduleEvents(schedule, false)) {
          fullRefresh()
          return
        }
      }

      for (const event of data) scheduleEventStore.set(event.id, event, instanceKeyRef.current)
      paintedPositionsRef.current = incomingPositions
      onPatchedRef.current?.()
      if (schedule.element) {
        for (const event of updates) {
          applyColors(schedule.element, event, getColorsRef.current)
        }
      }
      return
    }

    const { missingIds } = patchLiveScheduleEvents(dataSourceRef.current, diff.contentChanged)
    if (missingIds.length > 0) {
      fullRefresh()
      return
    }

    for (const event of data) scheduleEventStore.set(event.id, event, instanceKeyRef.current)

    if (schedule.element) {
      for (const event of diff.contentChanged) {
        applyColors(schedule.element, event, getColorsRef.current)
      }
    }
  }, [data, scheduleObj])

  useEffect(() => {
    const pending = pendingPositionPaintRef.current
    if (!pending) return
    if (pending !== dataSourceRef.current) return

    pendingPositionPaintRef.current = null
    schedulePaintRetry(pending)
  }, [dataSource])

  useEffect(() => {
    const onVisible = () => syncVisiblePositionsRef.current()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
      if (paintRetryRafRef.current) cancelAnimationFrame(paintRetryRafRef.current)
      pendingPositionPaintRef.current = null
    }
  }, [])

  useEffect(() => {
    const key = instanceKey ?? ''
    // A remount (StrictMode) keeps the same data, so refill what the cleanup emptied.
    scheduleEventStore.hydrate(dataRef.current, key)
    return () => {
      scheduleEventStore.reset(key)
    }
  }, [instanceKey])

  return dataSource
}
